/**
 * Simple test component to verify typing effects integration
 */

import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Volume2, Sparkles, Play, Square } from 'lucide-react';
import {
  useTypingEffectsPreferences,
  TypingEffectsManager,
  createBasicTypingEffects
} from './index';
import { AudioTest } from '@/components/debug/audio-test';

export function TypingEffectsTest() {
  const [isActive, setIsActive] = useState(false);
  const [text, setText] = useState('');
  const [lastEffect, setLastEffect] = useState<string>('');
  const managerRef = useRef<TypingEffectsManager | null>(null);
  const { config, updateConfig } = useTypingEffectsPreferences();

  const initializeEffects = async () => {
    if (!managerRef.current) {
      managerRef.current = new TypingEffectsManager(config);
      
      // Create a mock editor for testing
      const mockEditor = {
        children: [{ text }],
        selection: null
      } as any;

      try {
        await managerRef.current.initialize(mockEditor);
        setIsActive(true);
        setLastEffect('Initialized successfully');
      } catch (error) {
        setLastEffect(`Error: ${error}`);
      }
    }
  };

  const stopEffects = () => {
    if (managerRef.current) {
      managerRef.current.dispose();
      managerRef.current = null;
    }
    setIsActive(false);
    setLastEffect('Stopped');
  };

  const testEffect = () => {
    if (managerRef.current) {
      managerRef.current.onTyping();
      setLastEffect(`Triggered at ${new Date().toLocaleTimeString()}`);
    }
  };

  const testHeartEffect = () => {
    if (managerRef.current && isActive) {
      // Temporarily set to hearts effect
      const originalConfig = { ...config };
      updateConfig({
        confettiType: 'hearts',
        visualEffectsEnabled: true
      });

      // Update manager and trigger effect
      managerRef.current.updateConfig({
        confettiType: 'hearts',
        currentSound: config.currentSound, // Keep current sound, don't force heartbeat
        visualEffectsEnabled: true,
        audioEnabled: config.audioEnabled // Respect current audio setting
      });
      managerRef.current.onTyping();
      setLastEffect('❤️ Hearts visual effect triggered! (audio uses current sound setting)');

      // Restore original config after a delay
      setTimeout(() => {
        updateConfig(originalConfig);
        if (managerRef.current) {
          managerRef.current.updateConfig(originalConfig);
        }
      }, 1000);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    
    if (isActive && managerRef.current) {
      managerRef.current.onTyping();
      setLastEffect('Typing effect triggered');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Volume2 className="h-5 w-5" />
          Typing Effects Test
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "Active" : "Inactive"}
          </Badge>
          <Badge variant="outline">
            Audio: {config.audioEnabled ? "On" : "Off"}
          </Badge>
          <Badge variant="outline">
            Visual: {config.visualEffectsEnabled ? "On" : "Off"}
          </Badge>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={initializeEffects}
            disabled={isActive}
            size="sm"
          >
            <Play className="mr-2 h-4 w-4" />
            Start
          </Button>
          
          <Button
            onClick={stopEffects}
            disabled={!isActive}
            variant="secondary"
            size="sm"
          >
            <Square className="mr-2 h-4 w-4" />
            Stop
          </Button>
          
          <Button
            onClick={testEffect}
            disabled={!isActive}
            variant="outline"
            size="sm"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Test
          </Button>

          <Button
            onClick={testHeartEffect}
            disabled={!isActive}
            variant="outline"
            size="sm"
            className="text-red-500 hover:text-red-600"
          >
            ❤️ Hearts
          </Button>
        </div>

        <Textarea
          value={text}
          onChange={handleTextChange}
          placeholder="Type here to test effects..."
          className="min-h-[100px]"
        />

        <div className="text-sm text-muted-foreground">
          Last effect: {lastEffect || 'None'}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-sm">Quick toggles:</label>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => updateConfig({ audioEnabled: !config.audioEnabled })}
              variant="outline"
              size="sm"
            >
              Audio: {config.audioEnabled ? "On" : "Off"}
            </Button>
            <Button
              onClick={() => updateConfig({ visualEffectsEnabled: !config.visualEffectsEnabled })}
              variant="outline"
              size="sm"
            >
              Visual: {config.visualEffectsEnabled ? "On" : "Off"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Audio Debug Test */}
    <AudioTest />
    </div>
  );
}
